import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { CSS } from '@wix/patterns-dnd-kit-utilities';
import { TableRowTestWrapper } from './TestWrappers';
import { classes, st } from './DraggableTableRow.st.css.js';
import { useDragAndDropContext } from './DragAndDropContext';
import { useSortableTableRow } from './useSortableTableRow';
import { DraggableRowContext } from './DraggableRowContext';
import { UseSortableArguments } from '@wix/patterns-dnd-kit-sortable';

export interface DraggableTableRowProps {
  id: number | string;
  index: number;
  isDragOverlay?: boolean;
  isDragAndDropDisabled?: boolean;
  children: React.ReactElement<
    React.HTMLAttributes<HTMLTableRowElement>,
    React.ComponentType<
      React.HTMLAttributes<HTMLTableRowElement> &
        React.RefAttributes<HTMLElement> & {
          isDragOverlay?: boolean;
        }
    >
  >;
  forwardedRef?: (el: HTMLElement | null | undefined) => void;
}

export const DraggableTableRow = React.memo(function _DraggableTableRow(
  props: DraggableTableRowProps,
) {
  const {
    children,
    id,
    index,
    isDragOverlay,
    isDragAndDropDisabled,
    forwardedRef,
  } = props;
  const { type: Tr, key, props: trProps } = children;

  const {
    onDragAndDropDisabledRowPointerDown,
    eventHandlers,
    onDropAnimationEnd,
    hideOverlayBottomShadow,
    useIsDropDisabled,
  } = useDragAndDropContext();

  const dragHandleRef = useRef<HTMLElement | null>();

  useEffect(() => {
    if (onDropAnimationEnd) {
      return onDropAnimationEnd(id, dragHandleRef);
    }

    return;
  }, [id, onDropAnimationEnd]);

  const isDropDisabled = useIsDropDisabled?.(id);

  const disabled: UseSortableArguments['disabled'] = {
    draggable: false,
    droppable: isDropDisabled,
  };

  const {
    attributes,
    listeners,
    transform,
    transition,
    setNodeRef,
    isDragging,
  } = useSortableTableRow({
    id,
    disabled,
  });

  const draggableRowContextValue = useMemo(
    () => ({
      isDragOverlay,
    }),
    [isDragOverlay],
  );

  const ref = useCallback(
    (el: HTMLElement | null) => {
      forwardedRef?.(el);
      setNodeRef(el);
    },
    [forwardedRef, setNodeRef],
  );

  if (isDragAndDropDisabled) {
    return (
      <Tr
        key={key}
        {...trProps}
        onPointerDown={(e) => {
          trProps.onPointerDown?.(e);
          onDragAndDropDisabledRowPointerDown?.(id, e.nativeEvent);
        }}
        ref={(el) => {
          forwardedRef?.(el);
        }}
      />
    );
  }

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const rowEvents = eventHandlers?.row?.({ id });

  let tr = (
    <Tr
      key={key}
      {...trProps}
      className={st(
        classes.root,
        {
          dragging: isDragging,
          overlay: isDragOverlay,
          hideOverlayBottomShadow,
        },
        trProps.className,
      )}
      style={{
        ...trProps.style,
        ...style,
      }}
      {...attributes}
      {...listeners}
      ref={ref}
      tabIndex={undefined}
      {...rowEvents}
      onPointerDown={(e) => {
        trProps?.onPointerDown?.(e);
        listeners?.onPointerDown?.(e);
        rowEvents?.onPointerDown?.(e);
      }}
    />
  );

  if (process.env.NODE_ENV === 'test') {
    tr = (
      <TableRowTestWrapper
        {...eventHandlers?.row?.({ id, disabled })}
        id={id}
        index={index}
      >
        {tr}
      </TableRowTestWrapper>
    );
  }

  return (
    <DraggableRowContext.Provider value={draggableRowContextValue}>
      {tr}
    </DraggableRowContext.Provider>
  );
});
